"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const bcp_1 = require("@iov/bcp");
const crypto_1 = require("@iov/crypto");
const encoding_1 = require("@iov/encoding");
const address_1 = require("./address");
const atomicswapcontracttransactionbuilder_1 = require("./atomicswapcontracttransactionbuilder");
const constants_1 = require("./constants");
const encoding_2 = require("./encoding");
const erc20tokentransactionbuilder_1 = require("./erc20tokentransactionbuilder");
const serialization_1 = require("./serialization");
const serializationcommon_1 = require("./serializationcommon");
const transactionbuilder_1 = require("./smartcontracts/transactionbuilder");
const utils_1 = require("./utils");
class EthereumCodec {
    constructor(options) {
        this.atomicSwapEtherContractAddress = options.atomicSwapEtherContractAddress;
        this.atomicSwapErc20ContractAddress = options.atomicSwapErc20ContractAddress;
        this.customSmartContractConfig = options.customSmartContractConfig;
        this.erc20Tokens = options.erc20Tokens || new Map();
    }
    static getMemoFromInput(input) {
        try {
            return encoding_1.fromUtf8(input);
        }
        catch (_a) {
            const hexstring = encoding_1.toHex(input);
            // split in space separated chunks up to 16 characters each
            return (hexstring.match(/.{1,16}/g) || []).join(" ");
        }
    }
    bytesToSign(unsigned, nonce) {
        return {
            bytes: serialization_1.Serialization.serializeUnsignedTransaction(unsigned, nonce, this.erc20Tokens, this.getAtomicSwapContractAddress(unsigned), this.getCustomSmartContractAddress()),
            prehashType: bcp_1.PrehashType.Keccak256,
        };
    }
    bytesToPost(signed) {
        return serialization_1.Serialization.serializeSignedTransaction(signed, this.erc20Tokens, this.getAtomicSwapContractAddress(signed.transaction), this.getCustomSmartContractAddress());
    }
    identifier(signed) {
        throw new Error(`Not implemented tx: ${signed}`);
    }
    parseBytes(bytes, chainId) {
        const json = JSON.parse(encoding_1.fromUtf8(bytes));
        const nonce = utils_1.decodeHexQuantityNonce(json.nonce);
        const value = utils_1.decodeHexQuantityString(json.value);
        const input = encoding_1.fromHex(utils_1.normalizeHex(json.input));
        const chain = {
            forkState: encoding_2.BlknumForkState.Forked,
            chainId: utils_1.fromBcpChainId(chainId),
        };
        const r = encoding_1.fromHex(utils_1.normalizeHex(json.r));
        const s = encoding_1.fromHex(utils_1.normalizeHex(json.s));
        const v = utils_1.decodeHexQuantity(json.v);
        const recoveryParam = encoding_2.getRecoveryParam(chain, v);
        const signature = new crypto_1.ExtendedSecp256k1Signature(r, s, recoveryParam);
        const signatureBytes = signature.toFixedLength();
        const message = serialization_1.Serialization.serializeGenericTransaction(nonce, json.gasPrice, json.gas, json.to, value, input, utils_1.encodeQuantity(chain.chainId));
        const messageHash = new crypto_1.Keccak256(message).digest();
        const signerPubkey = {
            algo: bcp_1.Algorithm.Secp256k1,
            data: crypto_1.Secp256k1.recoverPubkey(signature, messageHash),
        };
        const transaction = this.parseBytesBuildTransaction(input, json, value, chainId, signerPubkey);
        return {
            transaction: transaction,
            signatures: [
                {
                    nonce: nonce,
                    pubkey: signerPubkey,
                    signature: signatureBytes,
                },
            ],
        };
    }
    identityToAddress(identity) {
        return address_1.pubkeyToAddress(identity.pubkey);
    }
    isValidAddress(address) {
        return address_1.isValidAddress(address);
    }
    getCustomSmartContractAddress() {
        const config = this.customSmartContractConfig;
        if (config === undefined) {
            return undefined;
        }
        return config.address;
    }
    getAtomicSwapContractAddress(unsigned) {
        const maybeSwapId = bcp_1.isSwapTransaction(unsigned) ? unsigned.swapId : undefined;
        return (maybeSwapId &&
            (maybeSwapId.prefix === serializationcommon_1.SwapIdPrefix.Ether
                ? this.atomicSwapEtherContractAddress
                : this.atomicSwapErc20ContractAddress));
    }
    parseBytesBuildTransaction(input, json, value, chainId, signerPubkey) {
        const fee = {
            gasLimit: utils_1.decodeHexQuantityString(json.gas),
            gasPrice: {
                quantity: utils_1.decodeHexQuantityString(json.gasPrice),
                fractionalDigits: constants_1.constants.primaryTokenFractionalDigits,
                tokenTicker: constants_1.constants.primaryTokenTicker,
            },
        };
        const atomicSwapContractAddress = [
            this.atomicSwapEtherContractAddress,
            this.atomicSwapErc20ContractAddress,
        ].find((address) => address !== undefined && address_1.toChecksummedAddress(json.to).toLowerCase() === address.toLowerCase());
        const erc20Token = [...this.erc20Tokens.values()].find((options) => options.contractAddress.toLowerCase() === address_1.toChecksummedAddress(json.to).toLowerCase());
        if (this.customSmartContractConfig) {
            return transactionbuilder_1.CustomSmartContractTransactionBuilder.buildTransaction(input, json, chainId, fee, signerPubkey, this.customSmartContractConfig);
        }
        else if (atomicSwapContractAddress) {
            const prefix = atomicSwapContractAddress === this.atomicSwapErc20ContractAddress
                ? serializationcommon_1.SwapIdPrefix.Erc20
                : serializationcommon_1.SwapIdPrefix.Ether;
            return atomicswapcontracttransactionbuilder_1.AtomicSwapContractTransactionBuilder.buildTransaction(input, this.erc20Tokens, chainId, value, fee, signerPubkey, atomicSwapContractAddress, prefix);
        }
        else if (erc20Token) {
            return erc20tokentransactionbuilder_1.Erc20TokenTransactionBuilder.buildTransaction(input, json, erc20Token, chainId, fee, signerPubkey);
        }
        else {
            const memo = EthereumCodec.getMemoFromInput(input);
            return {
                kind: "bcp/send",
                chainId: chainId,
                sender: address_1.pubkeyToAddress(signerPubkey),
                fee: fee,
                amount: {
                    quantity: utils_1.decodeHexQuantityString(json.value),
                    fractionalDigits: constants_1.constants.primaryTokenFractionalDigits,
                    tokenTicker: constants_1.constants.primaryTokenTicker,
                },
                recipient: address_1.toChecksummedAddress(json.to),
                memo: memo,
            };
        }
    }
}
exports.EthereumCodec = EthereumCodec;
/** An unconfigured EthereumCodec for backwards compatibility */
exports.ethereumCodec = new EthereumCodec({});
//# sourceMappingURL=ethereumcodec.js.map